import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@fontsource-variable/inter";
import "@fontsource-variable/jetbrains-mono";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { ToastProvider } from "./app/ToastProvider";
import { withAppBasePath } from "./app/basePath";
import { createAppRouter } from "./app/router";
import { AuthProvider } from "./features/auth/AuthProvider";
import { LocaleProvider } from "./i18n";
import "./styles.css";
import { captureCollectionHandoff } from "./features/qae-collection/handoff";

const LEGACY_ROUTES: Record<string, (tail: string) => string> = {
  "new-run": () => "/new-run",
  tasks: () => "/new-run",
  suites: (tail) => tail ? `/suites/${encodeURIComponent(tail)}` : "/suites",
  queue: () => "/queue",
  reports: () => "/reports",
  "run-detail": (tail) => tail ? `/runs/${encodeURIComponent(tail)}` : "/reports",
  failures: (tail) => tail ? `/runs/${encodeURIComponent(tail)}/failures` : "/reports",
  logs: (tail) => tail ? `/logs/${encodeURIComponent(tail)}` : "/logs",
  settings: (tail) => {
    const section = tail || "security";
    if (section === "environment") return "/projects";
    if (section === "judge") return "/projects";
    return `/settings/${encodeURIComponent(section)}`;
  },
};

function migrateLegacyHash(): void {
  const raw = window.location.hash.replace(/^#\/?/, "");
  if (!raw) return;
  const queryIndex = raw.indexOf("?");
  const legacyPath = queryIndex >= 0 ? raw.slice(0, queryIndex) : raw;
  const legacyQuery = queryIndex >= 0 ? raw.slice(queryIndex + 1) : "";
  const [view, ...parts] = legacyPath.split("/");
  let tail = "";
  try {
    tail = decodeURIComponent(parts.join("/"));
  } catch {
    window.history.replaceState({}, "", withAppBasePath("/invalid-legacy-link"));
    return;
  }
  const target = LEGACY_ROUTES[view]?.(tail);
  if (target) window.history.replaceState({}, "", `${withAppBasePath(target)}${legacyQuery ? `?${legacyQuery}` : ""}`);
}

captureCollectionHandoff();
migrateLegacyHash();
const router = createAppRouter();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
    mutations: { retry: false },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <LocaleProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider>
            <RouterProvider router={router} />
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </LocaleProvider>
  </StrictMode>,
);
